import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { APIProvider } from '@vis.gl/react-google-maps'
import { Toaster } from 'sonner'

import './index.css'
import { router } from './app/router'
import { mountNucleusPanel } from './integrations/nucleus'
import { queryClient } from './app/query-client'
import { ErrorBoundary } from './app/error-boundary'
import { ThemeProvider } from './design-system/theme-provider'

async function bootstrap() {
  // Start MSW when VITE_USE_MSW=true. Handlers live under src/test/msw-handlers.ts
  // (shared with vitest tests). Never enabled in production builds.
  if (import.meta.env.VITE_USE_MSW === 'true' && import.meta.env.DEV) {
    const { worker } = await import('./test/msw-browser')
    await worker.start({ onUnhandledRequest: 'bypass' })
  }

  const rootEl = document.getElementById('root')
  if (!rootEl) throw new Error('#root not found in index.html')

  const mapsKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''

  // Nucleus assistant panel — no-op unless VITE_NUCLEUS_* are configured.
  mountNucleusPanel(router)

  createRoot(rootEl).render(
    <StrictMode>
      <ErrorBoundary>
        <ThemeProvider defaultTheme="system">
          <QueryClientProvider client={queryClient}>
            {/*
              APIProvider loads the Google Maps JS API once. Geometry library
              is needed for polygon area math (see lib/polygon.ts). Marker
              library is needed for AdvancedMarkerElement used by the field-ops
              live map pins. Drawing is used by the plot-editor drawing tools.
              If the key is missing, the map components render an empty state —
              feature surfaces don't crash.
            */}
            <APIProvider apiKey={mapsKey} libraries={['geometry', 'drawing', 'marker']}>
              <RouterProvider router={router} />
            </APIProvider>
            <Toaster
              richColors
              closeButton
              position="bottom-right"
              toastOptions={{ duration: 5000 }}
            />
          </QueryClientProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </StrictMode>,
  )
}

void bootstrap()
